import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { BaseFormComponent } from '@classes/extends';
import { environment } from '@env/environment';
import { Environment } from '@env/environment.enum';
import {
    Cheptel, ControleperfService as ControlePerformancesService,
    DocumentExportable, SuiviExport,
    TypeFichier
} from '@estel-numerique/galaclick-api';
import { DateHelper } from '@estel-numerique/galaclick-core';
import { Constantes } from '@services/util/constantes';
import { isBefore } from 'date-fns';
import { lastValueFrom } from 'rxjs';
import { DialogGalaComponent } from '../composants/dialog-gala/dialog-gala.component';
import { InputGala } from '../composants/inputs/input.component';
import { SelectGala } from '../composants/select/select.component';
import { DateGalaComponent } from '../composants/date-gala/date-gala.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton } from '@angular/material/button';

@Component({
    templateUrl: './massExport.component.html',
    styleUrls: ['./massExport.component.scss'],
    imports: [DialogGalaComponent, InputGala, SelectGala, FormsModule, ReactiveFormsModule, DateGalaComponent, MatCheckbox, MatButton]
})
export class MassExportComponent extends BaseFormComponent implements OnInit {
    private formBuilder = inject(FormBuilder);
    private wsControlePerformances = inject(ControlePerformancesService);
    private dialogRef = inject<MatDialogRef<MassExportComponent>>(MatDialogRef);

    public codeModule: string = Constantes.CodeOption.CDOCBILAN; // Code module/option testé pour vérifier l'accès à cette fonction (si null = OK)
    public titre: string;
    protected lgTrad: Record<string, string> = {};
    public listBlocs: { texte: string; valeur: string; recette?: Environment }[];
    public cheptel: Cheptel;
    public selectTypeFichier: TypeFichier;
    private selectionDeselectionBoolean = true;
    public selectionDeselectionTitle: string;
    public listeTypesFichiers: { label: string; value: TypeFichier }[];
    public listeDocuments: { label: string; value: DocumentExportable; disabled: boolean }[];
    public selectDocument: DocumentExportable;
    public environmentValeur: number;
    public nuchepErrors: { nuchep: string; error: string }[] = [];
    public csvErrors: string;
    public lastSuiviExport: SuiviExport;

    public formData = this.formBuilder.group({
        NUCHEPS: ['', [Validators.required]],
        MAIL: ['', [Validators.required]],
        DDEBUT: ['', [Validators.required]],
        DFIN: ['', [Validators.required]],
    });

    public blocsFormData = this.formBuilder.group({
        AVECMONTROUPEAU: [true],
        AVECPILOTAGEREPRO: [true],
        AVECMESRESULTATS: [true],
        AVECLAITERIE: [true],
        AVECALIMENTATION: [true],
        AVECQUALITELAIT: [true]
    });

    constructor() {
        super();
        this.environmentValeur = environment.environment;
        this.lgTrad = {
            CLIENT: this.translate.instant("CLIENT"),
            DEBUT: this.translate.instant('DEBUT'),
            FIN: this.translate.instant('FIN'),
            TITRE: this.translate.instant("APPEXPORTMASSE.TITRE"),
            DEBUTPERIODE: this.translate.instant('APPBILANSYNTHESE.DEBUTPERIODE'),
            FINPERIODE: this.translate.instant('APPBILANSYNTHESE.FINPERIODE'),
            NUCHEP: this.translate.instant('NUCHEP'),
            MAIL: this.translate.instant('MAIL'),
            NUCHEPS: this.translate.instant('NUCHEPS'),
            NUMERO_CHEPTELS: this.translate.instant('NUMERO_CHEPTELS'),
            CALCUL: this.translate.instant('APPBILANSYNTHESE.CALCUL'),
            CALCULER: this.translate.instant('APPBILANSYNTHESE.CALCULER'),
            AVECMONTROUPEAU: this.translate.instant('APPBILANSYNTHESE.BLOCS.AVECMONTROUPEAU'),
            AVECSANTE: this.translate.instant('APPBILANSYNTHESE.BLOCS.AVECSANTE'),
            AVECREPRO: this.translate.instant('APPBILANSYNTHESE.BLOCS.AVECREPRO'),
            AVECPILOTAGEREPRO: this.translate.instant('APPBILANSYNTHESE.BLOCS.AVECPILOTAGEREPRO'),
            AVECMESRESULTATS: this.translate.instant('APPBILANSYNTHESE.BLOCS.AVECMESRESULTATS'),
            AVECLAITERIE: this.translate.instant('APPBILANSYNTHESE.BLOCS.AVECLAITERIE'),
            AVECALIMENTATION: this.translate.instant('APPBILANSYNTHESE.BLOCS.AVECALIMENTATION'),
            AVECQUALITELAIT: this.translate.instant('APPBILANSYNTHESE.BLOCS.AVECQUALITELAIT'),
            BLOCSAAFFICHER: this.translate.instant('APPBILANSYNTHESE.BLOCSAAFFICHER'),
            TOUTSELECTIONNER: this.translate.instant('APPBILANSYNTHESE.TOUTSELECTIONNER'),
            TOUTDESELECTIONNER: this.translate.instant('APPBILANSYNTHESE.TOUTDESELECTIONNER')
        };
        this.selectionDeselectionTitle = this.lgTrad.TOUTDESELECTIONNER;
        this.listeTypesFichiers = [{
            label: 'PDF', value: TypeFichier.Pdf
        },
        {
            label: 'EXCEL', value: TypeFichier.Excel
        }];
        this.selectTypeFichier = TypeFichier.Pdf;
    }

    public async setUpForChosenDocument(value: DocumentExportable) {
        if (value != undefined) {
            const result = await lastValueFrom(this.wsControlePerformances.getLastSuiviExport(value.nom));
            this.lastSuiviExport = result;

            if (value.nom == 'Bilan de synthèse') {

                this.listBlocs = [
                    { texte: this.lgTrad.AVECMONTROUPEAU, valeur: "AVECMONTROUPEAU" },
                    { texte: this.lgTrad.AVECPILOTAGEREPRO, valeur: "AVECPILOTAGEREPRO" },
                    { texte: this.lgTrad.AVECMESRESULTATS, valeur: "AVECMESRESULTATS" },
                    { texte: this.lgTrad.AVECLAITERIE, valeur: "AVECLAITERIE" },
                    { texte: this.lgTrad.AVECALIMENTATION, valeur: "AVECALIMENTATION" },
                    { texte: this.lgTrad.AVECQUALITELAIT, valeur: "AVECQUALITELAIT" }
                ];

                this.initCochesBlocs();
            }
            else if (value.nom == 'Gala Troupo') {
                this.listBlocs = [
                    { texte: this.lgTrad.AVECMESRESULTATS, valeur: "AVECMESRESULTATS" },
                    { texte: this.lgTrad.AVECREPRO, valeur: "AVECREPRO" },
                    { texte: this.lgTrad.AVECSANTE, valeur: "AVECSANTE" },
                    { texte: this.lgTrad.AVECALIMENTATION, valeur: "AVECALIMENTATION" },
                    { texte: this.lgTrad.AVECQUALITELAIT, valeur: "AVECQUALITELAIT" }
                ];
                this.initCochesBlocs();
            }
        }
        else {
            this.blocsFormData = undefined;
        }
    }

    ngOnInit() {
        super.ngOnInit();
        this.init().catch(e => this.util.fErreur(e));
    }

    // =========================================================================
    // CHARGEMENT CONTEXTE
    // =========================================================================

    private async chargeDonnees() {
        try {
            const dateMinus1Month = new Date();
            dateMinus1Month.setMonth(new Date().getMonth() - 1);
            this.formData.patchValue({
                DDEBUT: DateHelper.fConvDateTypescript(dateMinus1Month),
                DFIN: DateHelper.fConvDateTypescript(new Date()),
                MAIL: this.getUserSession().MAIL
            });

            const result = await lastValueFrom(this.wsControlePerformances.getDocumentsExportableEnMasse());

            this.listeDocuments = [];
            for (const elem of result) {
                this.listeDocuments.push({ label: elem.nom, value: elem, disabled: elem.disabled });
            }

            //A enlever une fois qu'on aura + d'1 document
            this.selectDocument = this.listeDocuments[0].value;
            await this.setUpForChosenDocument(this.selectDocument);
        } catch (e) {
            this.util.fErreur(e);
        }
    }

    private async init() {
        try {
            this.util.showLoader();
            await this.chargeDonnees();
        } catch (e) {
            this.util.fErreur(e);
        } finally {
            this.util.hideLoader();
        }
    }

    public async checkCheptels() {
        this.validationFormulaire(this.formData);
        if (this.selectDocument == null) {
            this.util.fErreur("Un document doit être choisi");
        }
        else if (this.formData.valid) {
            const date = new Date(this.formData.getRawValue().DFIN);
            date.setUTCFullYear(date.getUTCFullYear() - 1, date.getUTCMonth(), date.getDate());
            if (date > new Date(this.formData.getRawValue().DDEBUT)) {
                this.util.fErreur("La période ne peut être supérieure à une année");
                return;
            }
            this.formData.patchValue({ NUCHEPS: this.formData.getRawValue().NUCHEPS.replace(' ', '') });
            try {
                this.util.showLoader();
                const dateFin: string = this.formData.getRawValue().DFIN;
                this.nuchepErrors = [];
                const exportMasseObject = { nucheps: this.formData.getRawValue().NUCHEPS, typeFichier: this.selectTypeFichier, documentEnvoyable: this.selectDocument };

                this.csvErrors = await lastValueFrom(this.wsControlePerformances.checkNucheps(dateFin, exportMasseObject)) as string;
                if (this.csvErrors == undefined || this.csvErrors.length == 0) {
                    this.util.hideLoader();
                    this.proceedToMassExport();
                    return;
                }
                //Le résultat est de la forme nuchep;erreur;nuchep;erreur; etc.
                const splittedCsv = this.csvErrors.split(";");
                for (let i = 0; i < splittedCsv.length; i += 2) {
                    this.nuchepErrors.push({ nuchep: splittedCsv[i], error: splittedCsv[i + 1] });
                }
            } catch (e) {
                this.util.fErreur(e);
            } finally {
                this.util.hideLoader();
            }
        }
    }

    public proceedToMassExport() {
        const dateFin: string = this.formData.getRawValue().DFIN;
        const dateDebut: string = this.formData.getRawValue().DDEBUT;
        const blocs: boolean[] = [];
        const nomBlocs: string[] = [];
        let key: string;
        for (const elem of this.listBlocs) {
            key = elem.valeur;
            blocs.push(this.blocsFormData.get(key).value == true);
            nomBlocs.push(elem.texte);
        }
        try {
            const exportMasseObject = { nucheps: this.formData.getRawValue().NUCHEPS, typeFichier: this.selectTypeFichier, documentEnvoyable: this.selectDocument, csvErrors: this.csvErrors };
            lastValueFrom(this.wsControlePerformances.exportMasse(dateDebut, dateFin, this.formData.getRawValue().MAIL, exportMasseObject, blocs, nomBlocs)).catch(e => console.error(e));

            this.util.fInfo(`Votre export de masse est en cours de réalisation et sera envoyé à ${this.formData.getRawValue().MAIL}`);
        } catch (e) {
            this.util.fErreur(e);
        }
    }

    public copyCsvErrorsToClipBoard() {
        this.util.copyToClipBoard(this.csvErrors);
    }

    //Nécessite d'être utilisé après que le reset du form ait été fait
    private initCochesBlocs() {
        this.listBlocs.forEach(element => {
            this.blocsFormData.get(element.valeur).setValue(true);
            if (!this.droitsAccess.write) {
                this.blocsFormData.get(element.valeur).disable();
            }
        });
    }

    public checkLastDayOfMonth() {
        const dateForm: Date = new Date(this.formData.getRawValue().DFIN);
        const lastDateMonth: Date = new Date(dateForm.getFullYear(), dateForm.getMonth() + 1, 0);
        if (lastDateMonth.getDate() != dateForm.getDate()) {
            this.util.fErreur(this.translate.instant("APPBILANSYNTHESE.LASTDAYERROR"));
            return false;
        }
        return true;
    }

    public checkDateBeforeToday() {
        if (!isBefore(this.formData.getRawValue().DFIN, new Date())) {
            this.util.fErreur(this.translate.instant("APPBILANSYNTHESE.TODAYDAYERROR"));
            return false;
        }
        return true;
    }

    public hideModal() {
        this.formData.reset();
        this.formData.markAsPristine();
        this.formData.markAsUntouched();
        this.dialogRef.close();
    }

    public inverserSelectionBlocs() {
        this.selectionDeselectionBoolean = !this.selectionDeselectionBoolean;
        if (!this.selectionDeselectionBoolean) {
            this.selectionDeselectionTitle = this.lgTrad.TOUTSELECTIONNER;
        }
        else {
            this.selectionDeselectionTitle = this.lgTrad.TOUTDESELECTIONNER;
        }
        this.listBlocs.forEach(element => {
            if (element.recette >= this.environmentValeur) {
                this.formData.get(element.valeur).setValue(this.selectionDeselectionBoolean);
            }
        });
    }
}
